import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Dialog, DialogReason, DetailKey, DetailRow, ModalAction } from '@overlayqa/shared/ui';
import { INTEGRATION_STATUS_UNAVAILABLE_COPY, SYNC_RECONNECT_ACTION_LABEL } from '@overlayqa/shared';
import '@overlayqa/shared/ui/styles.css';
import '@overlayqa/shared/ui/dialog.css';
import { capture } from '@/lib/analytics';
import { IntegrationStatusChip, type IntegrationConnection, type IntegrationStatus } from './integrationStatus';
import { INTEGRATION_FACTS, INTEGRATION_SUMMARY, disconnectReason, type IntegrationKey } from './integrationDetails';

interface IntegrationDialogProps {
  provider: IntegrationKey;
  name: string;
  tile: ReactNode;
  status: IntegrationStatus;
  connection: IntegrationConnection;
  loading: boolean;
  errors: string[];
  onClose: () => void;
  /** Extra rows while connected (Slack's Channel row), after the connection's own. */
  children?: ReactNode;
}

const sinceDate = (iso: string) => new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });

/** A Status row with a sentence under the chip (why it cannot be read or made): stacked, as DetailRow stacks text. */
function StatusWithReason({ status, reason, alert = false }: { status: IntegrationStatus; reason: string; alert?: boolean }) {
  return (
    <div className="oq-metadata__row oq-detail-row--stack">
      <DetailKey>Status</DetailKey>
      <span className="oq-detail-chips"><IntegrationStatusChip status={status} /></span>
      <span className={`oq-detail-text${alert ? ' text-red-600' : ''}`} role={alert ? 'alert' : undefined}>{reason}</span>
    </div>
  );
}

/**
 * An integration's details on the shared Dialog (standard tier): its mark and name, one sentence on what it does, a
 * detail card with the Status first, the connection's rows and the integration's facts, and the action that fits the
 * status. Disconnect is a second step on the same dialog with Keep connected first and focused (DiscardDraftDialog).
 */
export function IntegrationDialog({ provider, name, tile, status, connection, loading, errors, onClose, children }: IntegrationDialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const focusRef = useRef<HTMLButtonElement>(null);
  const [step, setStep] = useState<'details' | 'confirm'>('details');
  const id = useId();
  const titleId = `${id}-title`;
  const reasonId = `${id}-reason`;
  const linked = status === 'connected' || status === 'reconnect';
  const { connecting, disconnecting } = connection;

  useEffect(() => {
    if (ref.current && !ref.current.open) ref.current.showModal();
  }, []);
  // Each step opens on its own first choice: Connect, Reconnect, or Keep connected; otherwise the dialog's Close.
  useEffect(() => { focusRef.current?.focus(); }, [step]);
  // A finished disconnect (or a connection gone some other way) returns to the details, which now offer Connect.
  const wasDisconnecting = useRef(disconnecting);
  useEffect(() => {
    if (step === 'confirm' && ((wasDisconnecting.current && !disconnecting) || !linked)) setStep('details');
    wasDisconnecting.current = disconnecting;
  }, [disconnecting, linked, step]);

  const close = () => {
    ref.current?.close();
    onClose();
  };
  const disconnectEvent = (s: 'asked' | 'kept' | 'confirmed') =>
    capture('integration_disconnect_clicked', { provider, placement: 'integration_dialog', step: s });

  if (step === 'confirm') {
    return (
      <Dialog dialogRef={ref} width="standard" icon="unlink" title={`Disconnect ${name}?`} titleId={titleId} descriptionId={reasonId}
        onClose={close} onCancel={(e) => { e.preventDefault(); close(); }} onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        footer={<>
          <button key="keep" ref={focusRef} type="button" className="oq-btn oq-btn--secondary" disabled={disconnecting}
            onClick={() => { disconnectEvent('kept'); setStep('details'); }}>
            <ModalAction kind="cancel">Keep connected</ModalAction>
          </button>
          <button key="confirm-disconnect" type="button" className="oq-btn oq-btn--danger" disabled={disconnecting}
            onClick={() => { disconnectEvent('confirmed'); connection.onDisconnect(); }}>
            <ModalAction kind="unlink">{disconnecting ? 'Disconnecting...' : 'Disconnect'}</ModalAction>
          </button>
        </>}>
        <DialogReason id={reasonId}>{disconnectReason(provider, name)}</DialogReason>
        {errors.map((error) => <p key={error} className="text-xs text-red-600 mt-2" role="alert">{error}</p>)}
      </Dialog>
    );
  }

  const connect = (event: 'integration_link_clicked' | 'integration_reconnect_clicked') => {
    capture(event, { provider, placement: 'integration_dialog' });
    connection.onConnect();
  };
  // Every action has its own key: React reusing one button as another (Keep connected becoming Connect) faded white
  // to blue on screen (mini run 2026-11-02).
  const footer = status === 'unknown'
    ? <button key="retry" type="button" className="oq-btn oq-btn--secondary" disabled={loading} onClick={connection.onRetry}><ModalAction kind="retry">Retry</ModalAction></button>
    : status === 'unavailable'
      ? <button key="connect-unavailable" type="button" className="oq-btn oq-btn--primary" disabled title={connection.unavailableReason ?? undefined}><ModalAction kind="link">Connect</ModalAction></button>
      : status === 'disconnected'
        ? <button key="connect" ref={focusRef} type="button" className="oq-btn oq-btn--primary" disabled={connecting} onClick={() => connect('integration_link_clicked')}>
          <ModalAction kind="link">{connecting ? 'Connecting...' : 'Connect'}</ModalAction>
        </button>
        : <>
          <button key="disconnect" type="button" className="oq-btn oq-btn--secondary" disabled={connecting || disconnecting}
            onClick={() => { disconnectEvent('asked'); setStep('confirm'); }}>
            <ModalAction kind="unlink">Disconnect</ModalAction>
          </button>
          {status === 'reconnect' && (
            <button key="reconnect" ref={focusRef} type="button" className="oq-btn oq-btn--primary" disabled={connecting || disconnecting}
              onClick={() => connect('integration_reconnect_clicked')}>
              <ModalAction kind="retry">{connecting ? 'Reconnecting...' : SYNC_RECONNECT_ACTION_LABEL}</ModalAction>
            </button>
          )}
        </>;

  const statusRow = status === 'unknown'
    ? <StatusWithReason status={status} reason={INTEGRATION_STATUS_UNAVAILABLE_COPY} alert />
    : status === 'unavailable' && connection.unavailableReason
      ? <StatusWithReason status={status} reason={connection.unavailableReason} />
      : <DetailRow label="Status" value={<>
        <IntegrationStatusChip status={status} />
        {linked && connection.connectedAt && <span className="oq-metadata__row-value">since {sinceDate(connection.connectedAt)}</span>}
      </>} />;

  return (
    <Dialog dialogRef={ref} width="standard" mark={tile} title={name} titleId={titleId} descriptionId={reasonId}
      onClose={close} onCancel={(e) => { e.preventDefault(); close(); }} onClick={(e) => { if (e.target === e.currentTarget) close(); }}
      footer={footer}>
      <DialogReason id={reasonId}>{INTEGRATION_SUMMARY[provider]}</DialogReason>
      {errors.map((error) => <p key={error} className="text-xs text-red-600 mt-2" role="alert">{error}</p>)}
      <div className="oq-auto-review-ui oq-detail-card mt-4">
        {statusRow}
        {linked && connection.rows?.map(([label, value]) => value ? <DetailRow key={label} label={label} value={value} /> : null)}
        {linked && connection.twoWaySync && (status === 'reconnect' && connection.reconnectMessage
          ? <DetailRow label="Two-way sync" value={connection.reconnectMessage} tone="warning" />
          : <DetailRow label="Two-way sync" value="On" />)}
        {linked && children}
        {INTEGRATION_FACTS[provider].map(([label, value]) => typeof value === 'string'
          ? <DetailRow key={label} label={label} value={value} />
          : <DetailRow key={label} label={label} list={[...value]} />)}
      </div>
    </Dialog>
  );
}
